import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface RemoveBrokerModalProps {
  isOpen: boolean;
  onClose: () => void;
  brokerageId: string;
}

export const RemoveBrokerModal = ({ isOpen, onClose, brokerageId }: RemoveBrokerModalProps) => {
  const [selectedBrokers, setSelectedBrokers] = useState<string[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch brokers in the brokerage
  const { data: brokers = [] } = useQuery({
    queryKey: ['brokers', brokerageId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brokers')
        .select('*')
        .eq('brokerage_id', brokerageId);
      
      if (error) {
        console.error('Error fetching brokers:', error);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!brokerageId && isOpen
  });

  const removeBrokersMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('brokers')
        .delete()
        .in('id', selectedBrokers);

      if (error) {
        console.error('Error removing brokers:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-all-conversations'] });
      toast({
        title: 'Success',
        description: `${selectedBrokers.length} broker(s) removed successfully`,
      });
      setSelectedBrokers([]);
      setShowConfirmDialog(false);
      onClose();
    },
    onError: (error) => {
      console.error('Failed to remove brokers:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove brokers. Please try again.',
        variant: 'destructive',
      });
    }
  });

  const handleBrokerToggle = (brokerId: string) => {
    setSelectedBrokers(prev => 
      prev.includes(brokerId)
        ? prev.filter(id => id !== brokerId)
        : [...prev, brokerId]
    );
  };

  const handleRemove = () => {
    if (selectedBrokers.length > 0) {
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmRemove = () => {
    removeBrokersMutation.mutate();
  };

  const handleClose = () => {
    setSelectedBrokers([]);
    setShowConfirmDialog(false);
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Brokers</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {brokers.length === 0 ? (
              <p className="text-slate-500 text-center py-4">
                No brokers found in this brokerage.
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Select brokers to remove:
                </p>
                {brokers.map((broker) => (
                  <div key={broker.id} className="flex items-center space-x-3 p-3 border rounded-lg">
                    <Checkbox
                      checked={selectedBrokers.includes(broker.id)}
                      onCheckedChange={() => handleBrokerToggle(broker.id)}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{broker.full_name}</p>
                      {broker.email && (
                        <p className="text-sm text-slate-500">{broker.email}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemove}
              disabled={selectedBrokers.length === 0}
              variant="destructive"
            >
              Remove Selected ({selectedBrokers.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Removal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {selectedBrokers.length} broker(s)? 
              This action cannot be undone. Any conversations assigned to these brokers will become unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmRemove}
              disabled={removeBrokersMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {removeBrokersMutation.isPending ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};